// Single source of truth for home page content. The testimonials and
// faqItems arrays also feed the Review/FAQPage JSON-LD in page.tsx, so
// visible copy and structured data cannot drift apart.

export interface Destination {
  name: string;
  slug: string; // used as /show/{slug}; spaces are valid ("new york")
  image: string;
  imageAlt: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface FeatureRow {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  image: string;
  imageAlt: string;
}

export interface Testimonial {
  quote: string;
  name: string;
  title: string;
  datePublished: string;
  schemaName: string;
}

export interface TrendingTrip {
  destination: string;
  title: string;
}

export interface HowItWorksStep {
  title: string;
  description: string;
}

export interface HeroSlide {
  desktop: string;
  mobile: string;
  alt: string;
}

export const heroSlides: HeroSlide[] = [
  {
    desktop: "/heroimages/maldives.avif",
    mobile: "/heroimages/maldives_mobile.avif",
    alt: "Overwater villas in the Maldives at sunset",
  },
  {
    desktop: "/heroimages/mumbai.avif",
    mobile: "/heroimages/mumbai_mobile.avif",
    alt: "Mumbai skyline along Marine Drive",
  },
  {
    desktop: "/heroimages/paris.avif",
    mobile: "/heroimages/paris_mobile.avif",
    alt: "Paris cityscape with the Eiffel Tower",
  },
];

export const popularDestinations: Destination[] = [
  {
    name: "Paris",
    slug: "paris",
    image: "/cityimages/paris.jpg",
    imageAlt: "Eiffel Tower and Paris rooftops",
  },
  {
    name: "Bali",
    slug: "bali",
    image: "/cityimages/bali.jpg",
    imageAlt: "Tropical temple and rice terraces in Bali",
  },
  {
    name: "New York",
    slug: "new york",
    image: "/cityimages/newyork.jpg",
    imageAlt: "Manhattan skyline in New York City",
  },
  {
    name: "Tokyo",
    slug: "tokyo",
    image: "/cityimages/tokyo.jpg",
    imageAlt: "Tokyo streets with Mount Fuji in the distance",
  },
  {
    name: "Rome",
    slug: "rome",
    image: "/images/blog/rome-cover.jpg",
    imageAlt: "The Colosseum in Rome, Italy",
  },
  {
    name: "London",
    slug: "london",
    image: "/images/blog/london-cover.jpg",
    imageAlt: "Big Ben and the Houses of Parliament in London",
  },
  {
    name: "Dubai",
    slug: "dubai",
    image: "/images/blog/dubai-cover.jpg",
    imageAlt: "Dubai skyline with the Burj Khalifa",
  },
  {
    name: "Maui",
    slug: "maui",
    image: "/images/blog/maui-cover.jpg",
    imageAlt: "Beaches and palm trees on Maui, Hawaii",
  },
];

export const footerDestinations = popularDestinations.slice(0, 4);

export const trendingTrips: TrendingTrip[] = [
  { destination: "paris", title: "3 Day Trip To Paris" },
  { destination: "bali", title: "Bali Beach Vacation" },
  { destination: "new york", title: "New York Travel Plan" },
];

export const howItWorksSteps: HowItWorksStep[] = [
  {
    title: "Enter Your Destination",
    description:
      "Simply type in where you want to go and let our AI do the rest",
  },
  {
    title: "Get Personalized Itineraries",
    description:
      "Receive a custom travel plan with attractions, restaurants, and activities",
  },
  {
    title: "Customize Your Trip",
    description: "Adjust your plan on the fly with simple commands to the AI",
  },
];

export const featureRows: FeatureRow[] = [
  {
    id: "personalized",
    eyebrow: "AI Vacation Planner",
    title: "Personalized travel itineraries in seconds",
    body: "Plan your perfect vacation with our AI-powered travel itinerary generator. Get customized day-by-day plans for any destination worldwide, built from expert destination data and tailored to your preferences.",
    bullets: [
      "Day-by-day plans with attractions and restaurants",
      "Hidden gems and local favorites most tourists miss",
      "Destination-specific tips from thousands of places",
    ],
    image: "/images/travel_details.jpg",
    imageAlt: "AI-generated travel itinerary example for Paris",
  },
  {
    id: "customize",
    eyebrow: "Real-Time Updates",
    title: "Customize your travel plans instantly",
    body: "Easily modify your itinerary with our flexible travel planner. Add destinations, extend your trip, or refine your interests — our AI vacation planner adapts to your changing needs in real time.",
    bullets: [
      "Simple commands like “add more museums”",
      "Family-friendly, couples, solo, or group travel styles",
      "Instant updates without starting over",
    ],
    image: "/images/update.jpg",
    imageAlt: "Customizing a travel itinerary with AI Trip Maker",
  },
  {
    id: "roadtrip",
    eyebrow: "Road Trip Planner",
    title: "Road trips & city tours, optimized",
    body: "Design the perfect road trip or city exploration with route optimization built into your travel itinerary. Our AI maps efficient paths between attractions and suggests the ideal time to visit each stop.",
    bullets: [
      "Optimized routes with convenient stops",
      "Scenic viewpoints and can’t-miss detours",
      "Accommodation suggestions along the way",
    ],
    image: "/images/roadtrip.jpg",
    imageAlt: "AI-optimized road trip route planning across scenic locations",
  },
  {
    id: "free",
    eyebrow: "Free Travel Planner",
    title: "Free for every budget and trip length",
    body: "Start planning your dream vacation today with our free travel itinerary builder. From budget-friendly weekend getaways to multi-city international tours, skip hours of research without expensive travel agents.",
    bullets: [
      "100% free — no sign-up required",
      "Weekend getaways, day trips, or multi-week tours",
      "Budget-friendly picks: free attractions and affordable dining",
    ],
    image: "/images/45.jpg",
    imageAlt: "Budget travel planning with AI Trip Maker",
  },
];

export const testimonials: Testimonial[] = [
  {
    quote:
      "The AI suggested places I never would have found on my own. Best Paris trip ever!",
    name: "Sarah J",
    title: "Paris Explorer",
    datePublished: "Jan 20, 2025",
    schemaName: "AI Trip Maker",
  },
  {
    quote:
      "Found amazing hidden restaurants in Tokyo thanks to this itinerary planner.",
    name: "David L",
    title: "Tokyo Food Enthusiast",
    datePublished: "Oct 2, 2024",
    schemaName: "AI Travel Planning",
  },
  {
    quote:
      "Perfect for our weekend getaway to New York. Hit all the major attractions without wasting time.",
    name: "Emma R",
    title: "New York City Visitor",
    datePublished: "Dec 7, 2024",
    schemaName: "AI Travel Planning",
  },
];

export const faqItems: FaqItem[] = [
  {
    question: "How does AI trip planner work?",
    answer:
      "Our AI trip planner leverages the power of AI to create personalized travel itineraries. Just enter your destination, trip duration, and preferences, and our algorithm will generate a custom day-by-day plan tailored to your interests.",
  },
  {
    question: "Is AI Trip Maker free to use?",
    answer:
      "Yes! The basic AI Trip Maker is completely free to use. You can generate unlimited travel itineraries for any destination worldwide without paying anything.",
  },
  {
    question: "Can I modify the itinerary after it's created?",
    answer:
      "Absolutely! Once your initial itinerary is generated, you can easily modify it by adding new instructions like 'add more museums' or 'include more family-friendly activities' and our AI will update your plan accordingly.",
  },
  {
    question: "How accurate are the travel recommendations?",
    answer:
      "Our AI travel planner uses current data about attractions, restaurants, and activities to create realistic itineraries. While we strive for accuracy, we always recommend verifying opening hours and availability before your trip.",
  },
  {
    question: "What destinations are supported?",
    answer:
      "AI Trip Maker supports virtually any travel destination worldwide, from major cities like Paris, Tokyo and New York to smaller towns and regions. The quality of recommendations may vary based on the popularity of the destination.",
  },
  {
    question: "How do I plan a road trip with AI Trip Maker?",
    answer:
      "Planning a road trip with AI Trip Maker is simple! Just enter your starting point, destinations you want to visit, and trip duration. Our AI will create an optimized route with stops, attractions, and accommodation suggestions along the way, making road trip planning effortless.",
  },
  {
    question:
      "What makes AI Trip Maker different from other travel planning tools?",
    answer:
      "AI Trip Maker combines powerful AI with travel expertise to create highly personalized itineraries. Unlike traditional travel planning tools, we optimize your entire experience - from efficient routes for road trips to daily schedules that maximize your vacation time. Our tool adapts to your preferences in real-time, making travel planning faster and more enjoyable.",
  },
  {
    question: "Can AI Trip Maker plan trips on a budget?",
    answer:
      "Yes! Our travel itinerary generator includes options for every budget, with recommendations for free attractions, economical dining options, and cost-saving travel tips. Just mention your budget preferences and the AI will create an affordable plan without sacrificing experiences.",
  },
  {
    question: "Is it good for weekend getaways and short trips?",
    answer:
      "Definitely. AI Trip Maker creates condensed itineraries for weekend getaways and day trips that maximize your limited time, ensuring you experience the best attractions your destination has to offer even on a short escape.",
  },
];
